import { useEffect } from "react";
import useRoutePlannerStore from "../state/route-planner-store";

// Breathing room kept between the order box and the edge of the visible area
const SCROLL_PADDING_PX = 24;

/**
 * Scrolls the route planner table so an order box is visible. The table
 * scrolls on both axes and pins columns (driver/route info on the left, the
 * expanded "skipped" column on the right) with position: sticky, so the
 * visible area is the container minus those pinned cells and the header.
 */
const scrollOrderBoxIntoView = (orderBox: HTMLElement) => {
  const scrollContainer = orderBox.closest<HTMLElement>(
    ".MuiTableContainer-root",
  );
  if (!scrollContainer) {
    return;
  }

  const containerRect = scrollContainer.getBoundingClientRect();
  let visibleLeft = containerRect.left + scrollContainer.clientLeft;
  let visibleRight = visibleLeft + scrollContainer.clientWidth;
  let visibleTop = containerRect.top + scrollContainer.clientTop;
  const visibleBottom = visibleTop + scrollContainer.clientHeight;

  // Route planner rows render their cells as <th>
  const cell = orderBox.closest("td, th");
  if (cell?.parentElement) {
    let isAfterCell = false;
    for (const sibling of Array.from(cell.parentElement.children)) {
      if (sibling === cell) {
        isAfterCell = true;
        continue;
      }
      if (getComputedStyle(sibling).position !== "sticky") {
        continue;
      }
      const siblingRect = sibling.getBoundingClientRect();
      if (isAfterCell) {
        visibleRight = Math.min(visibleRight, siblingRect.left);
      } else {
        visibleLeft = Math.max(visibleLeft, siblingRect.right);
      }
    }
  }

  const header = scrollContainer.querySelector("thead");
  if (header) {
    visibleTop = Math.max(visibleTop, header.getBoundingClientRect().bottom);
  }

  const boxRect = orderBox.getBoundingClientRect();

  // Center horizontally so the stops around it are visible too
  let deltaX = 0;
  if (boxRect.left < visibleLeft || boxRect.right > visibleRight) {
    deltaX =
      boxRect.left + boxRect.width / 2 - (visibleLeft + visibleRight) / 2;
  }

  let deltaY = 0;
  if (boxRect.top < visibleTop) {
    deltaY = boxRect.top - visibleTop - SCROLL_PADDING_PX;
  } else if (boxRect.bottom > visibleBottom) {
    deltaY = boxRect.bottom - visibleBottom + SCROLL_PADDING_PX;
  }

  if (deltaX !== 0 || deltaY !== 0) {
    scrollContainer.scrollBy({
      left: deltaX,
      top: deltaY,
      behavior: "smooth",
    });
  }
};

/**
 * When an address is selected (e.g. by clicking its house on the map), scroll
 * its order box on the route planner timeline into view. Only reacts to the
 * most recently selected id changing, so realtime refreshes of the selection
 * don't yank the timeline around.
 */
export const useScrollSelectedOrderIntoView = () => {
  const lastSelectedId = useRoutePlannerStore(
    (state) => state.selectedAddresses[state.selectedAddresses.length - 1]?.id,
  );

  useEffect(() => {
    if (!lastSelectedId) {
      return;
    }

    // Wait a frame so the timeline has rendered the selection
    const frameId = requestAnimationFrame(() => {
      // Target the box itself, not the whole order wrapper: the wrapper also
      // holds the travel line to the next stop, so a long travel time would
      // pull the scroll into the empty travel gap
      const orderBox = document.querySelector<HTMLElement>(
        `[data-location-id="${CSS.escape(lastSelectedId)}"] [data-testid="location-address"]`,
      );
      if (orderBox) {
        scrollOrderBoxIntoView(orderBox);
      }
    });

    return () => cancelAnimationFrame(frameId);
  }, [lastSelectedId]);
};
